// Share Modal - debug-triggered scaffold for Phase 1.13.
//
// Phase 1.13 wires this surface behind `?debug=1` only. Production
// homepages don't expose a "Test share" button. The real per-game share
// triggers ship with each game module in Phase 2+.
//
// Composition: `mountModalShell({ width: 360 })` (per `_Notes - Share Modal.md`)
// + a stub WORD5 preview rendered through `formatShareText()` +
// `mountActionButton` for Share / Copy / Skip.
//
// Mockup: Vault/Design/Mockups/_Notes - Share Modal.md

import {
  mountActionButton,
  mountModalShell,
  type ButtonHandle,
  type ModalHandle,
} from '@nostr-gives/components';
import { formatShareText } from '@nostr-gives/share-formatter';
import type { ShareNote } from '@nostr-gives/shared';

export function openShareModalDebug(): void {
  const sampleNote: ShareNote = {
    gameName: 'WORD5',
    puzzleNumber: 614,
    score: 4,
    maxScore: 6,
    gridRows: [
      ['absent', 'absent', 'present', 'absent', 'absent'],
      ['absent', 'present', 'absent', 'present', 'absent'],
      ['present', 'correct', 'absent', 'correct', 'absent'],
      ['correct', 'correct', 'correct', 'correct', 'correct'],
    ],
  };

  const shareText = formatShareText(sampleNote);

  const content = document.createElement('div');
  content.className = 'share-modal-content';

  const preview = document.createElement('pre');
  preview.className = 'share-preview emoji-grid';
  preview.textContent = shareText;
  content.appendChild(preview);

  const status = document.createElement('p');
  status.className = 'modal-status';
  content.appendChild(status);

  const ctaCol = document.createElement('div');
  ctaCol.className = 'share-modal-ctas';
  content.appendChild(ctaCol);

  let modal: ModalHandle | null = null;
  let shareBtn: ButtonHandle | null = null;
  let copyBtn: ButtonHandle | null = null;
  let skipBtn: ButtonHandle | null = null;

  function destroy(): void {
    shareBtn?.destroy();
    copyBtn?.destroy();
    skipBtn?.destroy();
    modal?.destroy();
  }

  shareBtn = mountActionButton(ctaCol, {
    variant: 'primary',
    label: 'Share to Nostr',
    onClick: (): void => {
      status.textContent = 'Stub session: skipping publish. Wire in Phase 2.';
      delete status.dataset.kind;
    },
  });

  copyBtn = mountActionButton(ctaCol, {
    variant: 'secondary',
    label: 'Copy text',
    onClick: (): void => {
      void navigator.clipboard
        .writeText(shareText)
        .then(() => {
          status.textContent = 'Copied.';
          delete status.dataset.kind;
          setTimeout(() => {
            if (status.textContent === 'Copied.') status.textContent = '';
          }, 1500);
        })
        .catch(() => {
          status.textContent = 'Copy failed.';
          status.dataset.kind = 'error';
        });
    },
  });

  skipBtn = mountActionButton(ctaCol, {
    variant: 'secondary',
    label: 'Play without signing in',
    onClick: destroy,
  });

  modal = mountModalShell(document.body, {
    title: 'Share your result',
    subtitle: 'Debug preview (dev only)',
    width: 360,
    onClose: destroy,
    content,
  });
}
